import React from "react";
import classNames from "classnames";
import { ArrowUpOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Button, Skeleton, Typography } from "antd";
import MyCard from "../MyCard";
import Heading from "../Heading";
import TinyAreaChart from "../charts/TinyAreaChart";

const { Text } = Typography;

export interface SlideData {
  value: string;
  change?: string;
  isUp?: boolean;
  timeframe?: string;
  chartData?: number[];
  additionalInfo?: any;
}

interface BaseSlideProps {
  title: string;
  isLoading: boolean;
  data: SlideData;
  timeframes?: string[];
  activeTimeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
  chartColor?: string;
  children?: React.ReactNode;
}

const BaseSlide: React.FC<BaseSlideProps> = ({
  title,
  isLoading,
  data,
  timeframes,
  activeTimeframe,
  onTimeframeChange,
  chartColor = "#1890ff",
  children,
}) => (
    <MyCard
      size="small"
      title={
        <Heading
          title={
            <div className="flex items-center gap-2">
              <div className="!text-base">{title}</div>
              <InfoCircleOutlined className="text-black/45" />
            </div>
          }
        />
      }
      className="px-2 py-1"
      styles={{ body: { padding: "6px 0px" } }}
      extra={
        timeframes && activeTimeframe ? (
          <div className="flex gap-2">
            {timeframes.map(tf => (
              <Button
                key={tf}
                size="small"
                className={classNames(tf === activeTimeframe && "bg-blue-500 text-white")}
                onClick={() => onTimeframeChange && onTimeframeChange(tf)}
              >
                {tf}
              </Button>
            ))}
          </div>
        ) : data.timeframe ? (
          <Button size="small">{data.timeframe}</Button>
        ) : null
      }
    >
      <div className="w-full">
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 1 }} className="mt-2" />
        ) : children ? (
          children
        ) : (
          <div className="flex gap-3 items-end">
            <div className="flex flex-col gap-2 items-center">
              <div className="font-semibold py-1 px-5 text-blue-800 bg-blue-100 rounded-md">{data.value}</div>
              {data.change && (
                <div
                  className={classNames(
                    "flex items-center text-nowrap gap-1",
                    data.isUp ? "text-green-500" : "text-red-500",
                  )}
                >
                  {data.isUp ? (
                    <ArrowUpOutlined className="text-xs" />
                  ) : (
                    <ArrowUpOutlined className="transform rotate-180 text-xs" />
                  )}
                  <Text className={data.isUp ? "text-green-500" : "text-red-500"}>{data.change}</Text>
                </div>
              )}
            </div>

            {/* TinyArea chart */}
            {data.chartData && (
              <div className="w-full">
                <TinyAreaChart data={data.chartData} color={chartColor} />
              </div>
            )}
          </div>
        )}
      </div>
    </MyCard>
  );

export default BaseSlide;

